/**
 * Leviathan - Terminal Detail Page
 * Quasar Prime LLC
 *
 * View terminal details, rack prices, and recent liftings
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Edit,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Warehouse,
  Plus,
  X,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface RackPrice {
  id: string;
  effectiveDate: string;
  grossPrice: number;
  netPrice: number | null;
  federalTax: number;
  stateTax: number;
  localTax: number;
  otherTaxes: number;
  totalWithTax: number | null;
  source: string | null;
  product: {
    id: string;
    name: string;
    sku: string;
  };
}

interface Lifting {
  id: string;
  liftingNumber: string;
  scheduledDate: string;
  status: string;
  grossGallonsLoaded: number | null;
  netGallonsLoaded: number | null;
  totalCost: number | null;
}

interface Terminal {
  id: string;
  terminalCode: string;
  name: string;
  displayName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  county: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  tcn: string | null;
  fein: string | null;
  stateLicenseNumber: string | null;
  operator: string | null;
  operatingHours: string | null;
  isActive: boolean;
  notes: string | null;
  supplier: {
    id: string;
    name: string;
    supplierNumber: string;
  } | null;
  rackPrices: RackPrice[];
  liftings: Lifting[];
}

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  EN_ROUTE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  AT_TERMINAL: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  LOADING: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  LOADED: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  DEPARTED: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50',
  COMPLETED: 'bg-green-500/20 text-green-400 border-green-500/50',
  CANCELLED: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
};

export default function TerminalDetail() {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [showAddPriceModal, setShowAddPriceModal] = useState(false);

  const { data: terminal, isLoading } = useQuery<Terminal>({
    queryKey: ['terminal', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch terminal');
      return res.json();
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !terminal?.isActive }),
      });
      if (!res.ok) throw new Error('Failed to update terminal');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal', id] });
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Group rack prices by product
  const pricesByProduct = terminal?.rackPrices.reduce((acc, price) => {
    const key = price.product.id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(price);
    return acc;
  }, {} as Record<string, RackPrice[]>) || {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!terminal) {
    return (
      <div className="text-center py-12">
        <Warehouse className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 mb-4">Terminal not found</p>
        <Link to="/terminals" className="text-amber-400 hover:text-amber-300">
          Back to Terminals
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/terminals"
            className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-100">{terminal.name}</h1>
              {!terminal.isActive && (
                <span className="px-2 py-1 text-xs bg-slate-700 text-slate-400 rounded">
                  Inactive
                </span>
              )}
            </div>
            {terminal.tcn && (
              <p className="text-amber-400 font-mono mt-1">{terminal.tcn}</p>
            )}
            <p className="text-slate-400 text-sm mt-1">
              <MapPin className="w-3 h-3 inline mr-1" />
              {terminal.city}, {terminal.state} {terminal.zip}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={`/terminals/${id}/edit`}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Link>
          <button
            onClick={() => toggleActiveMutation.mutate()}
            disabled={toggleActiveMutation.isPending}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              terminal.isActive
                ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
            }`}
          >
            {terminal.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <Link
            to={`/liftings/new?terminalId=${id}`}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Lifting
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Recent Liftings"
          value={terminal.liftings.length.toString()}
        />
        <StatCard
          label="Products"
          value={Object.keys(pricesByProduct).length.toString()}
        />
        <StatCard
          label="Latest ULSD"
          value={
            Object.values(pricesByProduct).flat().find(p => p.product.sku.includes('ULSD'))
              ? formatCurrency(Object.values(pricesByProduct).flat().find(p => p.product.sku.includes('ULSD'))!.grossPrice) + '/gal'
              : 'N/A'
          }
        />
        <StatCard label="State" value={terminal.state || '-'} />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Current Rack Prices */}
          <Panel
            title="Current Rack Prices"
            action={
              <button
                onClick={() => setShowAddPriceModal(true)}
                className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Price
              </button>
            }
          >
            {Object.keys(pricesByProduct).length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No rack prices recorded yet
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(pricesByProduct).map(([productId, prices]) => {
                  const latest = prices[0];
                  if (!latest) return null;
                  return (
                    <div
                      key={productId}
                      className="flex items-center justify-between py-3 border-b border-slate-700/50 last:border-0"
                    >
                      <div>
                        <div className="font-medium text-slate-100">
                          {latest.product.name}
                        </div>
                        <div className="text-slate-500 text-xs">
                          As of {formatDate(latest.effectiveDate)}
                          {latest.source && ` • ${latest.source}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-amber-400 font-bold text-lg">
                          {formatCurrency(latest.grossPrice)}
                          <span className="text-sm">/gal</span>
                        </div>
                        <div className="text-slate-500 text-xs">
                          +Tax: {formatCurrency(latest.federalTax + latest.stateTax + latest.localTax)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>

          {/* Recent Liftings */}
          <Panel title="Recent Liftings">
            {terminal.liftings.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No liftings recorded yet
              </div>
            ) : (
              <div className="space-y-3">
                {terminal.liftings.map((lifting) => (
                  <Link
                    key={lifting.id}
                    to={`/liftings/${lifting.id}`}
                    className="block p-3 bg-slate-900/50 border border-slate-700/50 rounded-lg hover:border-amber-500/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="text-amber-400 font-mono font-semibold">
                            {lifting.liftingNumber}
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs border rounded ${
                              STATUS_COLORS[lifting.status] || STATUS_COLORS.SCHEDULED
                            }`}
                          >
                            {lifting.status}
                          </span>
                        </div>
                        <div className="text-slate-500 text-xs mt-1">
                          {formatDate(lifting.scheduledDate)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-slate-100">
                          {lifting.netGallonsLoaded?.toLocaleString() || '-'} gal
                        </div>
                        {lifting.totalCost && (
                          <div className="text-amber-400 text-sm">
                            {formatCurrency(lifting.totalCost)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* Right Column - Info */}
        <div className="space-y-6">
          {/* Terminal Info */}
          <Panel title="Terminal Information">
            <div className="space-y-4">
              <InfoRow label="Code" value={terminal.terminalCode} />
              <InfoRow label="Address" value={terminal.address || '-'} />
              <InfoRow label="City" value={terminal.city || '-'} />
              <InfoRow label="State" value={terminal.state || '-'} />
              <InfoRow label="ZIP" value={terminal.zip || '-'} />
              {terminal.county && <InfoRow label="County" value={terminal.county} />}
              {terminal.operatingHours && (
                <InfoRow label="Hours" value={terminal.operatingHours} />
              )}
            </div>
          </Panel>

          {/* Tax Compliance */}
          {(terminal.tcn || terminal.fein || terminal.stateLicenseNumber) && (
            <Panel title="Tax Compliance">
              <div className="space-y-4">
                {terminal.tcn && <InfoRow label="TCN" value={terminal.tcn} />}
                {terminal.fein && <InfoRow label="FEIN" value={terminal.fein} />}
                {terminal.stateLicenseNumber && (
                  <InfoRow label="State License" value={terminal.stateLicenseNumber} />
                )}
              </div>
            </Panel>
          )}

          {/* Operator */}
          {(terminal.operator || terminal.supplier) && (
            <Panel title="Operator">
              <div className="space-y-4">
                {terminal.operator && <InfoRow label="Operator" value={terminal.operator} />}
                {terminal.supplier && (
                  <InfoRow label="Supplier" value={terminal.supplier.name} />
                )}
              </div>
            </Panel>
          )}

          {/* Contact */}
          {(terminal.phone || terminal.email || terminal.fax) && (
            <Panel title="Contact">
              <div className="space-y-4">
                {terminal.phone && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Phone className="w-4 h-4 text-slate-500" />
                    {terminal.phone}
                  </div>
                )}
                {terminal.email && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-4 h-4 text-slate-500" />
                    {terminal.email}
                  </div>
                )}
                {terminal.fax && (
                  <InfoRow label="Fax" value={terminal.fax} />
                )}
              </div>
            </Panel>
          )}

          {/* Notes */}
          {terminal.notes && (
            <Panel title="Notes">
              <p className="text-slate-400 text-sm whitespace-pre-wrap">
                {terminal.notes}
              </p>
            </Panel>
          )}
        </div>
      </div>

      {/* Add Price Modal */}
      {showAddPriceModal && (
        <AddPriceModal
          terminalId={id!}
          onClose={() => setShowAddPriceModal(false)}
          onSuccess={() => {
            setShowAddPriceModal(false);
            queryClient.invalidateQueries({ queryKey: ['terminal', id] });
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl font-bold text-slate-100">{value}</div>
    </div>
  );
}

function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-700">
        <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-slate-100">{value}</div>
    </div>
  );
}

function AddPriceModal({
  terminalId,
  onClose,
  onSuccess,
}: {
  terminalId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    productId: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    grossPrice: '',
    federalTax: '0.244',
    stateTax: '0.20',
    localTax: '0',
    source: 'MANUAL',
  });

  // Fetch products
  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/inventory?limit=50`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json();
    },
  });

  const products = productsData?.items || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/terminals/rack-prices`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          terminalId,
          productId: formData.productId,
          effectiveDate: formData.effectiveDate,
          grossPrice: parseFloat(formData.grossPrice) || 0,
          federalTax: parseFloat(formData.federalTax) || 0,
          stateTax: parseFloat(formData.stateTax) || 0,
          localTax: parseFloat(formData.localTax) || 0,
          source: formData.source,
        }),
      });

      if (res.ok) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to add price:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotal = () => {
    return (
      (parseFloat(formData.grossPrice) || 0) +
      (parseFloat(formData.federalTax) || 0) +
      (parseFloat(formData.stateTax) || 0) +
      (parseFloat(formData.localTax) || 0)
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-slate-100">Add Rack Price</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Product *</label>
              <select
                value={formData.productId}
                onChange={(e) => setFormData((prev) => ({ ...prev, productId: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              >
                <option value="">Select...</option>
                {products.map((p: { id: string; name: string }) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Effective Date</label>
              <input
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, effectiveDate: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Rack Price ($/gal) *</label>
            <input
              type="number"
              value={formData.grossPrice}
              onChange={(e) => setFormData((prev) => ({ ...prev, grossPrice: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              step="0.0001"
              placeholder="0.0000"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Federal Tax</label>
              <input
                type="number"
                value={formData.federalTax}
                onChange={(e) => setFormData((prev) => ({ ...prev, federalTax: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.0001"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">State Tax</label>
              <input
                type="number"
                value={formData.stateTax}
                onChange={(e) => setFormData((prev) => ({ ...prev, stateTax: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.0001"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Local Tax</label>
              <input
                type="number"
                value={formData.localTax}
                onChange={(e) => setFormData((prev) => ({ ...prev, localTax: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.0001"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-sm mb-2">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            >
              <option value="MANUAL">Manual Entry</option>
              <option value="OPIS">OPIS</option>
              <option value="DTN">DTN</option>
            </select>
          </div>

          <div className="pt-4 border-t border-slate-700">
            <div className="flex items-center justify-between text-lg">
              <span className="text-slate-400">Total Cost:</span>
              <span className="text-amber-400 font-bold">${calculateTotal().toFixed(4)}/gal</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.productId || !formData.grossPrice}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Add Price'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
